import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ButtonComponent } from '@ppl/ui';
import { ConditionalLogic, DynamicForm, DynamicFormField, FieldOption, FormData, FormSection, TableColumn } from '../../../interfaces/dynamic-form.interface';
import { TimesheetService } from 'src/app/services/timesheet.service';
import { ChecklistItem } from 'src/app/interfaces/checklist.model';
import { CheckListService } from 'src/app/services/check-list.service';
import { HttpResponse } from '@angular/common/http';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { UserService } from 'src/app/services/user.service';
import { SectionsService } from 'src/app/services/sections.service';
import { StorageService } from 'src/app/services/storage.service';
import { AUTH_SERVICE_FREQUENCIES } from 'src/app/shared/constants/static-data.constants';
import { encodeBase64, parseEncodedJsonArray } from 'src/app/shared/utils/base64.util';
import { TAX_ID_TYPE, W9_FIELD_IDS } from 'src/app/shared/constants/static-data.constants';

@Component({
  selector: 'ppl-dynamic-form-renderer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzInputModule,
    NzRadioModule,
    NzCheckboxModule,
    NzSelectModule,
    NzDatePickerModule,
    NzAlertModule,
    NzIconModule,
    ButtonComponent,
  ],
  templateUrl: './dynamic-form-renderer.component.html',
  styleUrl: './dynamic-form-renderer.component.css',
})
export class DynamicFormRendererComponent implements OnInit, OnChanges {
  // region --- Inputs / Outputs ---
  @Input() formConfig!: DynamicForm;
  @Input() checklistItem?: ChecklistItem;
  @Input() providerId!: string;
  @Input() selectedProviderType?: string;
  @Input() selectedParticipantId!: number;
  @Input() programCode!: string;
  @Input() readonly: boolean = false;
  @Input() hideFormActions: boolean = false;
  // When set (Employer ⇄ Provider linked forms), save/sign go to the employer/provider form
  // endpoints instead of the participant-association endpoint.
  @Input() linkedSaveConfig?: { mode: 'employerProvider' | 'providerEmployer'; employerId: string; providerId: string; userType: string };

  @Output() formSaved = new EventEmitter<{
    checklistItemId: number;
    checklistItemDetailId: number;
  }>();
  @Output() formChanged = new EventEmitter<boolean>();
  // endregion

  // region --- Component state ---
  formGroup!: FormGroup;
  saveInProgress = false;
  saveError = false;
  errorMessage = '';
  isFormCompleted = false;
  hasChanges = false;
  source = 'provider';
  isParticipantFlow = false;
  userId = '';

  /** Number of rows currently shown for each `table` field, keyed by field id. */
  private tableRowCounts: Record<string, number> = {};
  /**
   * Number of rows that actually have controls, keyed by field id. A table with no `maxRows`
   * cap grows on demand (ensureTableRows) instead of pre-allocating, so this — not getMaxRows —
   * bounds every loop over a table's controls.
   */
  private tableBuiltRows: Record<string, number> = {};
  /** Per-cell pseudo-fields, so the template gets stable references and helpers can be reused. */
  private cellFields = new Map<string, DynamicFormField>();
  /** Cached cell arrays per table row, keyed by "<field id>|<row number>". */
  private rowCells = new Map<string, DynamicFormField[]>();

  /** Service codes (with their authList) fetched for the participant + provider. */
  private serviceCodes: any[] = [];
  /**
   * Readable text saved alongside an id-valued table cell, keyed by cell control name. Populated
   * from the saved row's `labelKey` on prefill so an option can still be labelled when the remote
   * option list is unavailable (see buildRemoteOptions).
   */
  private savedCellLabels = new Map<string, string>();
  /**
   * Whether the next authorization response may replace the table's rows. A signed form is
   * rebuilt from what was saved with it, so only a change to the agreement term re-opens it to
   * the latest authorizations.
   *
   * Always false on the participant side: the participant signs the agreement the provider
   * filled in and cannot edit the service details, so the saved rows are the only thing they
   * should ever see — never a fresh authorization lookup that may have moved on since.
   */
  private replaceRowsOnNextFetch = true;
  /** Bumped on every fetch so cached option arrays are invalidated. */
  private serviceCodesVersion = 0;
  /** Payload of the last lookup, so repeated prefills don't refire an identical request. */
  private lastServiceCodeRequestKey = '';
  /** Control name of the field each dependent field derives its options from. */
  private optionsParents = new Map<string, string>();
  /** Memoised option arrays — the template asks for these on every change detection pass. */
  private optionsCache = new Map<string, FieldOption[]>();
  // endregion

  constructor(
    private fb: FormBuilder,
    private checkListService: CheckListService,
    private dateFormatter: DateTimeFormatterService,
    private userService: UserService,
    private sectionsService: SectionsService,
    private storageService: StorageService,
    private timesheetService: TimesheetService,
  ) {}

  // ───────────────────────────────────────────────
  //  Lifecycle
  // ───────────────────────────────────────────────

  ngOnInit(): void {
    this.isParticipantFlow =
      this.userService.getLeadEntry() === 'participantEnrollment' ||
      this.sectionsService.getFlow() === 'participantEnrollment';
    this.source = this.isParticipantFlow ? 'participant' : 'provider';
    this.userId =
      this.storageService.getParticipantId() ||
      this.storageService.getProviderId() ||
      this.userService.getSelectedLead() ||
      '';
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formConfig'] && !changes['formConfig'].firstChange) {
      this.initializeForm();
      return;
    }

    if (changes['checklistItem'] && !changes['checklistItem'].firstChange) {
      this.saveInProgress = false;
      this.saveError = false;
      this.errorMessage = '';
      this.hasChanges = false;
      this.isFormCompleted = this.checklistItem?.formStatus === 'C';
    }
  }

  // ───────────────────────────────────────────────
  //  Form initialisation
  // ───────────────────────────────────────────────

  private initializeForm(): void {
    this.saveInProgress = false;
    this.saveError = false;
    this.errorMessage = '';
    this.hasChanges = false;
    this.isFormCompleted = this.checklistItem?.formStatus === 'C';

    const controls: Record<string, any> = {};
    this.tableRowCounts = {};
    this.tableBuiltRows = {};
    this.cellFields.clear();
    this.rowCells.clear();
    this.optionsParents.clear();
    this.optionsCache.clear();
    this.savedCellLabels.clear();
    this.replaceRowsOnNextFetch = !this.isParticipantFlow;
    this.serviceCodes = [];
    this.lastServiceCodeRequestKey = '';

    this.forEachField((field) => {
      if (field.optionsDependsOn) {
        this.optionsParents.set(field.id, field.optionsDependsOn);
      }

      if (field.type === 'table') {
        this.buildTableControls(field, controls);
        return;
      }

      const validators = this.buildValidators(field);
      const initialValue = this.getInitialValue(field);

      controls[field.id] = [
        { value: initialValue, disabled: field.disabled || this.readonly },
        validators,
      ];
    });

    this.formGroup = this.fb.group(controls);

    // When Height Feet changes, re-validate Height Inches so the cross-field
    // "both 0" error clears or appears correctly.
    this.formGroup.get('Height Feet')?.valueChanges.subscribe(() => {
      this.formGroup.get('Height Inches')?.updateValueAndValidity({ emitEvent: false });
    });

    // W-9: SSN Value/EIN Value are only known once prefill applies them (they aren't real PDF
    // fields the user types into) — re-validate the Taxpayer ID type radio whenever either
    // arrives or changes, so the "value not on file" error appears/clears correctly.
    for (const sourceFieldId of [W9_FIELD_IDS.SSN_VALUE, W9_FIELD_IDS.EIN_VALUE]) {
      this.formGroup.get(sourceFieldId)?.valueChanges?.subscribe(() => {
        this.formGroup.get(W9_FIELD_IDS.TAX_ID_TYPE)?.updateValueAndValidity({ emitEvent: false });
      });
    }

    // nz-radio-group only marks itself touched on blur, not on selection — so the very first
    // toggle away from the default wouldn't show the "value not on file" error until the user
    // clicked elsewhere. getErrorMessage() gates on touched, so mark it explicitly on every
    // selection instead of waiting for blur.
    this.formGroup.get(W9_FIELD_IDS.TAX_ID_TYPE)?.valueChanges.subscribe(() => {
      this.formGroup.get(W9_FIELD_IDS.TAX_ID_TYPE)?.markAsTouched();
    });

    this.formGroup.valueChanges.subscribe(() => {
      this.handleConditionalLogic();
      this.handleAutoCalculations();
      this.emitDirtyState();
    });

    this.watchOptionDependencies();
    this.watchServiceCodeDateFields();
    this.loadServiceCodes();

    // Initial conditional logic evaluation after the first change-detection cycle
    setTimeout(() => this.handleConditionalLogic(), 0);
  }

  private buildValidators(field: DynamicFormField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    const validValues = new Set(field.options?.map(opt => opt.value) ?? []);

    if (field.required) {
      const isMultiCheckbox =
        field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 1;
      if (isMultiCheckbox) {
        validators.push((control) =>
          Array.isArray(control.value) && control.value.length > 0 ? null : { required: true }
        );
      } else {
        validators.push(Validators.required);
      }
    }

    // Radio/dropdown values must match one of the defined options.
    // Prevents stale prefill values from bypassing required validation.
    if ((field.type === 'radio' || field.type === 'dropdown') && field.options?.length) {
      validators.push((control) => {
        if (control.value === null || control.value === undefined || control.value === '') return null;
        return validValues.has(control.value) ? null : { invalidOption: true };
      });
    }

    if (field.type === 'checkbox' && field.options?.length) {
      const isMultiCheckbox = field.options.length > 1;
      validators.push((control) => {
        if (control.value === null || control.value === undefined || control.value === '') return null;

        if (isMultiCheckbox) {
          if (!Array.isArray(control.value)) return { invalidOption: true };
          return control.value.every((value: any) => validValues.has(value)) ? null : { invalidOption: true };
        }

        return validValues.has(control.value) ? null : { invalidOption: true };
      });
    }

    for (const rule of field.validation ?? []) {
      switch (rule.type) {
        case 'email':
          validators.push(Validators.email);
          break;
        case 'min':
          if (rule.value !== undefined) validators.push(Validators.min(rule.value));
          break;
        case 'max':
          if (rule.value !== undefined) validators.push(Validators.max(rule.value));
          break;
        case 'pattern':
          if (rule.value) validators.push(Validators.pattern(rule.value));
          break;
      }
    }

    if (field.id === 'Routing Number' || field.id === 'Account Number') {
      validators.push(this.allZerosValidator());
    }

    if (field.id === 'Routing Number') {
      validators.push(this.abaRoutingChecksumValidator());
    }

    if (field.id === 'Height Inches') {
      validators.push(this.heightNotBothZeroValidator());
    }

    if (field.id === W9_FIELD_IDS.TAX_ID_TYPE) {
      validators.push(this.taxpayerIdValueMissingValidator());
    }

    return validators;
  }

  private allZerosValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();
      if (!value) return null;
      return /^0+$/.test(value) ? { allZeros: true } : null;
    };
  }

  private heightNotBothZeroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const inches = String(control.value ?? '');
      if (inches !== '0') return null;
      const feet = String(this.formGroup?.get('Height Feet')?.value ?? '');
      return feet === '0' ? { heightBothZero: true } : null;
    };
  }

  /** W-9: the selected Taxpayer ID type must have a value on file (SSN Value/EIN Value, sourced
   *  from the provider's identifiers) — otherwise there's nothing to sign the form with. */
  private taxpayerIdValueMissingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const type = control.value;
      const sourceFieldId = type === TAX_ID_TYPE.SSN ? W9_FIELD_IDS.SSN_VALUE
        : type === TAX_ID_TYPE.EIN ? W9_FIELD_IDS.EIN_VALUE
        : null;
      if (!sourceFieldId) return null;

      const sourceValue = this.formGroup?.get(sourceFieldId)?.value;
      return sourceValue ? null : { taxpayerIdValueMissing: type };
    };
  }

  private abaRoutingChecksumValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '').trim();
      if (!value || !/^\d{9}$/.test(value)) return null;

      const digits = value.split('').map(Number);
      const checksum =
        (digits[0] + digits[3] + digits[6]) * 3 +
        (digits[1] + digits[4] + digits[7]) * 7 +
        (digits[2] + digits[5] + digits[8]);

      return checksum % 10 === 0 ? null : { abaRouting: true };
    };
  }

  private getInitialValue(field: DynamicFormField): any {
    const isMultiCheckbox =
      field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 1;
    return isMultiCheckbox ? (field.defaultValue ?? []) : (field.defaultValue ?? null);
  }

  // ───────────────────────────────────────────────
  //  Remote dropdown options (participant authorizations)
  // ───────────────────────────────────────────────

  /**
   * Fetch the participant's authorized service codes, using the same endpoint and
   * payload shape as the add-invoice screen. The date range comes from whichever
   * form fields the config names, so the lookup follows the agreement term.
   */
  private loadServiceCodes(): void {
    const config = this.formConfig.serviceCodeSource;
    if (!config || !this.usesRemoteOptions()) return;

    const consumerId = this.selectedParticipantId;
    const periodFromDate = this.getDateFieldAsString(config.fromDateField);
    const periodToDate = this.getDateFieldAsString(config.toDateField);

    // Without a participant or a term to look up against there is nothing to ask for.
    if (!consumerId || !periodFromDate || !periodToDate) {
      this.lastServiceCodeRequestKey = '';
      this.setServiceCodes([]);
      return;
    }

    // The prefill and the two date watchers can all ask for the same term in quick
    // succession; only the first of those needs to hit the API.
    const requestKey = `${consumerId}|${periodFromDate}|${periodToDate}|${this.providerId}`;
    if (requestKey === this.lastServiceCodeRequestKey) return;
    this.lastServiceCodeRequestKey = requestKey;

    const payload = {
      programCode: this.programCode,
      consumerId,
      periodFromDate,
      periodToDate,
      source: config.source ?? 'invoice',
      providerId: this.providerId,
    };

    this.timesheetService.getServiceCodesByConsumer(payload).subscribe({
      next: (data: any) => {
        // The API reports an exhausted/absent authorization as a 404 body rather than an HTTP error.
        const isAuthFailure = data?.responsedata === 'Auth Fail' && data?.status === 404;
        this.setServiceCodes(isAuthFailure || !Array.isArray(data?.responsedata) ? [] : data.responsedata);
      },
      error: () => this.setServiceCodes([]),
    });
  }

  private setServiceCodes(serviceCodes: any[]): void {
    this.serviceCodes = serviceCodes;
    this.serviceCodesVersion++;
    this.optionsCache.clear();
    this.prefillServiceTables();
  }

  /**
   * Fill the service table from the authorizations just fetched — one row per service detail.
   * loadServiceCodes runs on the initial prefill and again from the term's date watchers, so
   * these rows replace whatever the table held rather than adding to it.
   *
   * Only columns the payload actually carries are written. Anything else — the provider's own
   * additional description — is carried across by the rest of the row's values, so refetching
   * the same term doesn't discard typing, while a row the new term no longer covers drops out
   * with it.
   *
   * Skipped entirely for a signed form on load: what was saved with the signature is the record
   * of what was agreed, so applyPrefillData's rows stand. Changing the agreement term clears
   * that hold and the latest authorizations take over again.
   *
   * Also skipped for the whole of the participant flow — those rows come from the saved base64
   * only. The participant has no provider id to look services up with, so the fetch comes back
   * empty and replacing on it would blank the very details they are being asked to sign.
   */
  private prefillServiceTables(): void {
    if (!this.formGroup) return;

    if (!this.replaceRowsOnNextFetch) {
      // Still drop the memoised options: the fetch that just landed is what labels the cells.
      this.optionsCache.clear();
      return;
    }

    this.forEachField((field) => {
      if (field.type !== 'table' || !field.columns?.some(column => column.optionsSource)) return;

      const records = this.buildServiceRowRecords();
      const columns = field.columns ?? [];
      // Columns the payload fills. The rest belong to the provider and are only carried over.
      const mapped = columns.filter(column =>
        column.optionsValueKey || records.some(record =>
          Object.prototype.hasOwnProperty.call(record, column.id)),
      );

      const carried = this.captureCarriedCells(field, mapped);
      this.ensureTableRows(field, records.length);
      const builtRows = this.getBuiltRows(field);

      // Exactly as many rows as the authorization returned — no minRows floor. These rows are
      // not the user's to add, so an empty lookup means an empty table rather than one blank
      // row inviting input that cannot be filled in.
      this.tableRowCounts[field.id] = Math.min(records.length, builtRows);

      for (let row = 1; row <= builtRows; row++) {
        const record = records[row - 1];
        const rowKey = record ? this.getRowKeyFromRecord(mapped, record) : '';

        for (const column of columns) {
          const controlName = this.getCellControlName(field, column, row);
          const control = this.formGroup.get(controlName);
          if (!control) continue;

          this.savedCellLabels.delete(controlName);

          if (!record) {
            control.setValue(null, { emitEvent: false });
            continue;
          }

          // The id-valued column stores the authorization id but reads as its description,
          // which nothing downstream can resolve back — so keep the text for the option list.
          if (column.optionsValueKey) {
            control.setValue(this.toCellValue(record[column.optionsValueKey]), { emitEvent: false });
            if (column.labelKey && record['desc']) {
              this.savedCellLabels.set(controlName, String(record['desc']));
            }
            continue;
          }

          if (Object.prototype.hasOwnProperty.call(record, column.id)) {
            control.setValue(this.toCellValue(record[column.id]), { emitEvent: false });
            continue;
          }

          control.setValue(carried.get(rowKey)?.[column.id] ?? null, { emitEvent: false });
        }
      }

      this.syncTableRowState(field, this.isFieldVisible(field));
    });

    // The rows just written are the current values the option lists have to keep selectable.
    this.optionsCache.clear();
  }

  /**
   * One record per service detail — the payload nests three levels deep (service code ->
   * authList -> serviceDetails), and it is the innermost entry that carries the rate, units,
   * frequency and service detail text. Each level is flattened onto the record, so a column
   * named after any property of any level fills without a mapping.
   *
   * A level with nothing under it still produces a row, so an authorization that has no
   * service details yet is visible rather than silently dropped.
   */
  private buildServiceRowRecords(): Record<string, any>[] {
    const records: Record<string, any>[] = [];

    for (const code of this.serviceCodes ?? []) {
      const { authList, ...codeValues } = code ?? {};
      const auths = Array.isArray(authList) ? authList : [];

      if (!auths.length) {
        records.push({ ...codeValues });
        continue;
      }

      for (const auth of auths) {
        const { serviceDetails, ...authValues } = auth ?? {};
        const details = Array.isArray(serviceDetails) ? serviceDetails : [];

        if (!details.length) {
          records.push({ ...codeValues, ...authValues });
          continue;
        }

        for (const detail of details) {
          records.push({ ...codeValues, ...authValues, ...detail });
        }
      }
    }

    return records;
  }

  /**
   * Snapshot the cells the payload does not supply, keyed by the payload-filled cells alongside
   * them, so they can be put back on the rows that survive the refetch. One authorization can
   * hold several service details, so the authorization id alone would not tell two of its rows
   * apart — the whole mapped row is the key.
   */
  private captureCarriedCells(
    field: DynamicFormField,
    mapped: TableColumn[],
  ): Map<string, Record<string, any>> {
    const carried = new Map<string, Record<string, any>>();
    if (!mapped.length) return carried;

    for (let row = 1; row <= this.getRowCount(field); row++) {
      const rowKey = mapped
        .map(column => String(this.formGroup.get(this.getCellControlName(field, column, row))?.value ?? ''))
        .join('|');
      if (!rowKey.replace(/\|/g, '')) continue;

      const values: Record<string, any> = {};
      for (const column of field.columns ?? []) {
        values[column.id] = this.formGroup.get(this.getCellControlName(field, column, row))?.value ?? null;
      }
      carried.set(rowKey, values);
    }

    return carried;
  }

  /** The same key captureCarriedCells builds, read off a fetched record instead of the form. */
  private getRowKeyFromRecord(mapped: TableColumn[], record: Record<string, any>): string {
    return mapped
      .map(column => this.toCellValue(record[column.optionsValueKey ?? column.id]) ?? '')
      .join('|');
  }

  /** Cells hold strings, so a saved row still matches its option after the backend round-trip. */
  private toCellValue(value: any): string | null {
    return value === null || value === undefined || value === '' ? null : String(value);
  }

  private usesRemoteOptions(): boolean {
    let found = false;
    this.forEachField((field) => {
      if (field.optionsSource) found = true;
      if (field.columns?.some(column => column.optionsSource)) found = true;
    });
    return found;
  }

  private getDateFieldAsString(fieldId?: string): string {
    if (!fieldId) return '';
    const value = this.formGroup?.get(fieldId)?.value;
    if (!value) return '';
    return value instanceof Date ? this.dateFormatter.formatDateToString(value) : String(value);
  }

  /** Refetch whenever the term driving the lookup changes. */
  private watchServiceCodeDateFields(): void {
    const config = this.formConfig.serviceCodeSource;
    if (!config || !this.usesRemoteOptions()) return;

    for (const fieldId of [config.fromDateField, config.toDateField]) {
      if (!fieldId) continue;
      this.formGroup.get(fieldId)?.valueChanges.subscribe(() => {
        // A new term means the saved rows no longer describe it, signed or not — take the
        // latest authorizations. Except on the participant side, where the rows are not
        // theirs to change and the saved ones keep standing.
        if (!this.isParticipantFlow) {
          this.replaceRowsOnNextFetch = true;
        }
        this.loadServiceCodes();
      });
    }
  }

  /**
   * Options for a field or table cell: the inline list when the JSON declares one,
   * otherwise the fetched authorization data. The current value is always included
   * so a saved selection still renders when its authorization is no longer returned.
   */
  getFieldOptions(field: DynamicFormField): FieldOption[] {
    if (!field.optionsSource) return field.options ?? [];

    const parentControlName = this.optionsParents.get(field.id);
    const parentValue = parentControlName ? this.formGroup?.get(parentControlName)?.value : null;
    const currentValue = this.formGroup?.get(field.id)?.value;
    const cacheKey = `${field.id}|${parentValue ?? ''}|${currentValue ?? ''}|${this.serviceCodesVersion}`;

    const cached = this.optionsCache.get(cacheKey);
    if (cached) return cached;

    const options = this.buildRemoteOptions(field, parentValue, currentValue);
    this.optionsCache.set(cacheKey, options);
    return options;
  }

  private buildRemoteOptions(field: DynamicFormField, parentValue: any, currentValue: any): FieldOption[] {
    let options: FieldOption[] = [];

    if (field.optionsSource === 'serviceCodes') {
      options = this.serviceCodes
        .map(code => this.toOption(code.serviceCodeDetail))
        .filter(option => !!option.value);
    } else if (field.optionsSource === 'serviceDescriptions') {
      const parentCode = this.serviceCodes.find(code => code.serviceCodeDetail === parentValue);
      options = (parentCode?.authList ?? [])
        .map((auth: any) => this.toAuthOption(auth, field.optionsValueKey))
        .filter((option: FieldOption) => !!option.value);
    } else if (field.optionsSource === 'serviceFrequencies') {
      // The agreement is printed for the participant, so the readable label is what is stored.
      options = AUTH_SERVICE_FREQUENCIES.map(frequency => this.toOption(frequency.label));
    }

    // Keep a previously saved value selectable even if it is no longer authorized.
    // Compared as text: a saved id can come back from the backend as a number.
    if (currentValue && !options.some(option => String(option.value) === String(currentValue))) {
      // A column with an optionsValueKey stores an id, so labelling the fallback option with the
      // raw value would show that id to the user — which is what happened on the participant side,
      // where the service-code fetch is skipped and no real option can resolve the label. The save
      // writes the readable text alongside the id under the column's labelKey, so prefer that.
      const savedLabel = this.savedCellLabels.get(field.id);
      options = [
        ...options,
        savedLabel
          ? { key: String(currentValue), label: savedLabel, value: String(currentValue) }
          : this.toOption(currentValue),
      ];
    }

    return options;
  }

  private toOption(value: any): FieldOption {
    const text = value == null ? '' : String(value);
    return { key: text, label: text, value: text };
  }

  /**
   * An authorization always reads as its description, but the column can choose to store
   * an identifying property instead — the label is what the user picks, the value is what
   * the payload carries.
   *
   * When the column names a key the authorization has no value for (a null
   * tasAuthorizationXProviderId, say), the record isn't selectable: there is nothing to
   * store for it. Blanking the value here lets the caller's `!!option.value` filter drop
   * it, rather than falling back to the description and saving prose where the backend
   * expects an id.
   */
  private toAuthOption(auth: any, valueKey?: string): FieldOption {
    const option = this.toOption(auth?.desc);
    if (!valueKey) return option;

    const id = auth?.[valueKey];
    if (id === null || id === undefined || id === '') {
      return { ...option, value: '' };
    }

    // Stored as a string like every other value in this payload, so a saved row still
    // matches its option after the round-trip through the backend.
    return { ...option, value: String(id) };
  }

  /** Clearing a parent selection invalidates whatever the dependent field held. */
  private watchOptionDependencies(): void {
    for (const [dependentName, parentName] of this.optionsParents) {
      this.formGroup.get(parentName)?.valueChanges.subscribe(() => {
        const dependent = this.formGroup.get(dependentName);
        if (dependent?.value) dependent.setValue(null, { emitEvent: false });
      });
    }
  }

  // ───────────────────────────────────────────────
  //  Table fields (repeating rows)
  // ───────────────────────────────────────────────

  /**
   * Create one control per column per row, up to maxRows. Every row exists in the
   * form group from the start so control names stay stable; rows beyond the current
   * row count are kept disabled (skipping validation) and blank.
   */
  private buildTableControls(field: DynamicFormField, controls: Record<string, any>): void {
    const initialRows = this.getMinRows(field);
    this.tableRowCounts[field.id] = initialRows;

    // A capped table keeps pre-allocating every row so control names stay stable; an uncapped
    // one starts at the minimum and grows through ensureTableRows as the data needs it.
    const rowsToBuild = field.maxRows ? this.getMaxRows(field) : initialRows;

    for (let row = 1; row <= rowsToBuild; row++) {
      Object.assign(controls, this.createTableRowControls(field, row, row <= initialRows));
    }

    this.tableBuiltRows[field.id] = rowsToBuild;
  }

  /** Controls for one table row, registered against the cell/row caches the template reads. */
  private createTableRowControls(
    field: DynamicFormField,
    row: number,
    isActiveRow: boolean,
  ): Record<string, any> {
    const controls: Record<string, any> = {};
    const cells: DynamicFormField[] = [];

    for (const column of field.columns ?? []) {
      const cell = this.createCellField(field, column, row);
      this.cellFields.set(cell.id, cell);
      // rowCells is what the template renders, so a hidden column keeps its control but
      // contributes no cell — leaving it in would put a stray <td> under the wrong header.
      if (!column.hidden) {
        cells.push(cell);
      }

      // A cascading column depends on another column *of the same row*.
      const parentColumn = field.columns?.find(c => c.id === column.optionsDependsOn);
      if (parentColumn) {
        this.optionsParents.set(cell.id, this.getCellControlName(field, parentColumn, row));
      }

      controls[cell.id] = [
        { value: null, disabled: !isActiveRow || !!column.disabled || this.readonly },
        this.buildValidators(cell),
      ];
    }

    this.rowCells.set(this.getRowKey(field, row), cells);
    return controls;
  }

  /** How many rows of this table currently have controls. */
  private getBuiltRows(field: DynamicFormField): number {
    return this.tableBuiltRows[field.id] ?? this.getMinRows(field);
  }

  /**
   * Grow an uncapped table until it has `needed` rows of controls. A capped table is already
   * fully built, and no table ever shrinks — row counts move, the controls behind them stay.
   */
  private ensureTableRows(field: DynamicFormField, needed: number): void {
    const target = Math.min(needed, this.getMaxRows(field));
    let built = this.getBuiltRows(field);
    if (target <= built) return;

    while (built < target) {
      built++;
      const definitions = this.createTableRowControls(field, built, true);
      for (const [name, [state, validators]] of Object.entries(definitions) as [string, [any, ValidatorFn[]]][]) {
        this.formGroup.addControl(name, this.fb.control(state, { validators }), { emitEvent: false });
      }
    }

    this.tableBuiltRows[field.id] = built;
  }

  /** A table cell behaves like a normal field whose id is the flattened row key. */
  private createCellField(field: DynamicFormField, column: TableColumn, row: number): DynamicFormField {
    return {
      ...column,
      id: this.getCellControlName(field, column, row),
      label: `${column.label} (row ${row})`,
    };
  }

  getCellControlName(field: DynamicFormField, column: TableColumn, row: number): string {
    return `${column.id} ${field.rowLabel ?? 'Row'} ${row}`;
  }

  /**
   * The row cap. Omitting `maxRows` means uncapped — the table takes as many rows as the data
   * gives it — so this is only a ceiling, never the count of rows that exist.
   */
  getMaxRows(field: DynamicFormField): number {
    return field.maxRows ? Math.max(field.maxRows, 1) : Number.MAX_SAFE_INTEGER;
  }

  /** True when the table has a declared ceiling worth showing to the user. */
  hasRowLimit(field: DynamicFormField): boolean {
    return !!field.maxRows;
  }

  getMinRows(field: DynamicFormField): number {
    return Math.min(Math.max(field.minRows ?? 1, 1), this.getMaxRows(field));
  }

  getRowCount(field: DynamicFormField): number {
    return this.tableRowCounts[field.id] ?? this.getMinRows(field);
  }

  /** Row numbers currently shown, for the template's @for. */
  getVisibleRows(field: DynamicFormField): number[] {
    return Array.from({ length: this.getRowCount(field) }, (_, index) => index + 1);
  }

  getRowCells(field: DynamicFormField, row: number): DynamicFormField[] {
    return this.rowCells.get(this.getRowKey(field, row)) ?? [];
  }

  /** Columns that get a header and a cell. A `hidden` column still has controls behind it. */
  getVisibleColumns(field: DynamicFormField): TableColumn[] {
    return (field.columns ?? []).filter(column => !column.hidden);
  }

  /** A cell the user can never edit, so it renders as text rather than a disabled control. */
  isCellReadonly(cell: DynamicFormField): boolean {
    return this.readonly || !!cell.disabled;
  }

  /**
   * What a read-only cell shows: the selected option's label for a dropdown — the control holds
   * an authorization id — and the raw value otherwise. A date is formatted the way its picker
   * would have displayed it.
   */
  getCellText(cell: DynamicFormField): string {
    const value = this.formGroup?.get(cell.id)?.value;
    if (value === '' || value === null || value === undefined) return '';

    if (cell.type === 'date') {
      const date = value instanceof Date ? value : null;
      return date ? this.dateFormatter.formatDateToString(date) : String(value);
    }

    const display = this.getCellDisplayValue(cell.id, value);
    return display === null || display === undefined ? '' : String(display);
  }

  /**
   * A submitted form keeps its row controls: a provider can come back to an agreement they
   * already signed, add a service and sign again, so only `readonly` closes the table.
   */
  showRowActions(field: DynamicFormField): boolean {
    return !this.readonly
      && (field.allowRemoveRow !== false || field.allowDuplicateRow !== false);
  }

  canDuplicateRow(field: DynamicFormField): boolean {
    return this.isTableEditable(field)
      && field.allowDuplicateRow !== false
      && this.getRowCount(field) < this.getMaxRows(field);
  }

  canAddRow(field: DynamicFormField): boolean {
    return this.isTableEditable(field)
      && field.allowAddRow !== false
      && this.getRowCount(field) < this.getMaxRows(field);
  }

  canRemoveRow(field: DynamicFormField): boolean {
    return this.isTableEditable(field)
      && field.allowRemoveRow !== false
      && this.getRowCount(field) > this.getMinRows(field);
  }

  addTableRow(field: DynamicFormField): void {
    if (!this.canAddRow(field)) return;

    this.ensureTableRows(field, this.getRowCount(field) + 1);
    this.tableRowCounts[field.id] = this.getRowCount(field) + 1;
    this.syncTableRowState(field, true);
    this.markTableChanged();
  }

  /** Remove a row and shift the rows below it up, so row numbers stay contiguous. */
  removeTableRow(field: DynamicFormField, row: number): void {
    if (!this.canRemoveRow(field)) return;

    const values = this.readTableValues(field);
    values.splice(row - 1, 1);
    values.push(this.createEmptyRowValues(field));

    this.tableRowCounts[field.id] = this.getRowCount(field) - 1;
    this.writeTableValues(field, values);
    this.syncTableRowState(field, true);
    this.markTableChanged();
  }

  /** Insert a copy of a row directly beneath it, shifting the rows below down. */
  duplicateTableRow(field: DynamicFormField, row: number): void {
    if (!this.canDuplicateRow(field)) return;

    this.ensureTableRows(field, this.getRowCount(field) + 1);

    const values = this.readTableValues(field);
    values.splice(row, 0, { ...values[row - 1] });
    values.length = this.getBuiltRows(field);

    this.tableRowCounts[field.id] = this.getRowCount(field) + 1;
    this.writeTableValues(field, values);
    this.syncTableRowState(field, true);
    this.markTableChanged();
  }

  private readTableValues(field: DynamicFormField): Record<string, any>[] {
    const values: Record<string, any>[] = [];

    for (let row = 1; row <= this.getBuiltRows(field); row++) {
      const rowValues: Record<string, any> = {};
      for (const column of field.columns ?? []) {
        rowValues[column.id] = this.formGroup.get(this.getCellControlName(field, column, row))?.value ?? null;
      }
      values.push(rowValues);
    }

    return values;
  }

  private writeTableValues(field: DynamicFormField, values: Record<string, any>[]): void {
    for (let row = 1; row <= this.getBuiltRows(field); row++) {
      for (const column of field.columns ?? []) {
        const control = this.formGroup.get(this.getCellControlName(field, column, row));
        control?.setValue(values[row - 1]?.[column.id] ?? null, { emitEvent: false });
      }
    }
  }

  private createEmptyRowValues(field: DynamicFormField): Record<string, any> {
    const rowValues: Record<string, any> = {};
    for (const column of field.columns ?? []) {
      rowValues[column.id] = null;
    }
    return rowValues;
  }

  /** Enable the cells of active rows and blank out (and disable) everything past the row count. */
  private syncTableRowState(field: DynamicFormField, visible: boolean): void {
    const rowCount = this.getRowCount(field);

    for (let row = 1; row <= this.getBuiltRows(field); row++) {
      const isActive = visible && row <= rowCount;

      for (const column of field.columns ?? []) {
        const control = this.formGroup.get(this.getCellControlName(field, column, row));
        if (!control) continue;

        if (isActive && !column.disabled && !this.readonly) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
          if (!isActive) control.setValue(null, { emitEvent: false });
        }
      }
    }
  }

  private isTableEditable(field: DynamicFormField): boolean {
    return !this.readonly && this.isFieldVisible(field);
  }

  private markTableChanged(): void {
    this.formGroup.markAsDirty();
    this.emitDirtyState();
  }

  private getRowKey(field: DynamicFormField, row: number): string {
    return `${field.id}|${row}`;
  }

  /**
   * Parse a saved table value into row objects; null when the value isn't a row array.
   * Accepts base64 as well as plain JSON, so it doesn't matter whether the backend
   * decodes what the form posts or stores it as-is.
   */
  private parseSavedTableRows(value: any): Record<string, any>[] | null {
    return parseEncodedJsonArray(value);
  }

  /** After a prefill, grow each table to cover the last row that carries data. */
  private syncTableRowCountsFromData(data: FormData): void {
    this.forEachField((field) => {
      if (field.type !== 'table') return;

      let lastPopulatedRow = 0;
      for (let row = 1; row <= this.getBuiltRows(field); row++) {
        const hasValue = (field.columns ?? []).some((column) => {
          const value = data[this.getCellControlName(field, column, row)];
          return value !== null && value !== undefined && value !== '';
        });
        if (hasValue) lastPopulatedRow = row;
      }

      this.tableRowCounts[field.id] = Math.max(this.getMinRows(field), lastPopulatedRow);
    });
  }

  // ───────────────────────────────────────────────
  //  Prefill / patch (called by parent)
  // ───────────────────────────────────────────────

  /** Patch form values without marking anything dirty. */
  applyPrefillData(data: FormData | null | undefined): void {
    if (!data || !this.formGroup) return;

    // Do not overwrite in-progress questionnaire edits with late prefill/cache updates.
    if (this.formGroup.dirty || this.hasChanges) {
      return;
    }

    // Reverse the mergeInto transform: if the target field's saved value matches
    // a mergeInto source field's option, restore the checkbox so it renders checked.
    // handleConditionalLogic will then hide and clear the text field automatically.
    const patchData = { ...data };
    this.forEachField((field) => {
      if (!field.mergeInto) return;
      const targetValue = patchData[field.mergeInto];
      const matchingOption = field.options?.find(opt => opt.value === targetValue);
      if (matchingOption) {
        patchData[field.id] = targetValue;
      }
    });

    // Reverse the table transform: a saved table arrives as a JSON array under the
    // table's own key, so expand it back into the per-cell controls.
    this.forEachField((field) => {
      if (field.type !== 'table') return;

      const rows = this.parseSavedTableRows(patchData[field.id]);
      if (!rows) return;

      delete patchData[field.id];
      // An uncapped table has only its minimum rows until something asks for more.
      this.ensureTableRows(field, rows.length);

      // A signed form is the record of what was agreed, so its own saved rows win over whatever
      // the authorization lookup now returns. Only a change to the term re-opens it (see
      // watchServiceCodeDateFields).
      if (this.isFormCompleted && rows.length) {
        this.replaceRowsOnNextFetch = false;
      }

      rows.slice(0, this.getBuiltRows(field)).forEach((rowValues, index) => {
        for (const column of field.columns ?? []) {
          const controlName = this.getCellControlName(field, column, index + 1);
          patchData[controlName] = rowValues?.[column.id] ?? null;

          // The save writes the readable text under the column's labelKey next to the id. Keep it
          // so buildRemoteOptions can label the cell without needing the remote option list.
          if (column.labelKey) {
            const savedLabel = rowValues?.[column.labelKey];
            if (typeof savedLabel === 'string' && savedLabel.trim()) {
              this.savedCellLabels.set(controlName, savedLabel);
            } else {
              this.savedCellLabels.delete(controlName);
            }
          }
        }
      });
    });

    // Grow the tables before patching, and patch silently: syncTableRowState blanks every
    // row past the current row count, and the formGroup.valueChanges subscription would
    // otherwise run it after each cell is written — wiping rows 2+ as they are restored.
    this.syncTableRowCountsFromData(patchData);
    this.formGroup.patchValue(patchData, { emitEvent: false });
    this.handleConditionalLogic();
    this.handleAutoCalculations();

    // The authorization lookup keys off the agreement term, which only exists once the
    // prefill lands — and the silent patch above deliberately doesn't fire the date
    // watchers. Without this the table's saved rows render their raw stored values
    // (an authorization id in place of the service description).
    this.loadServiceCodes();

    this.formGroup.markAsPristine();
    this.hasChanges = false;
    this.formChanged.emit(false);
  }

  // ───────────────────────────────────────────────
  //  Checkbox helpers (used by template)
  // ───────────────────────────────────────────────

  isMultiCheckboxSelected(fieldId: string, optionValue: any): boolean {
    const value = this.formGroup?.get(fieldId)?.value;
    return Array.isArray(value) && value.includes(optionValue);
  }

  onMultiCheckboxChange(fieldId: string, optionValue: any, checked: boolean): void {
    const control = this.formGroup?.get(fieldId);
    if (!control || control.disabled) return;

    const field = this.findFieldById(fieldId);
    const options = field?.options ?? [];
    const validValues = new Set(options.map(opt => opt.value));

    const current = Array.isArray(control.value)
      ? control.value.filter((v: any) => validValues.has(v))
      : [];

    let next: any[];

    if (checked) {
      const selectedOption = options.find(opt => opt.value === optionValue);
      const isNoneOfAbove = selectedOption?.noneOfAbove === true;

      if (isNoneOfAbove) {
        // "None of the above" selected — clear all other selections
        next = [optionValue];
      } else {
        // Regular option selected — remove any "none of the above" option
        const noneValues = new Set(options.filter(opt => opt.noneOfAbove).map(opt => opt.value));
        next = Array.from(new Set([...current.filter((v: any) => !noneValues.has(v)), optionValue]));
      }
    } else {
      next = current.filter((v: any) => v !== optionValue);
    }

    this.setControlValueAndMarkDirty(control, next);
  }

  isSingleCheckboxChecked(fieldId: string, optionValue: any): boolean {
    return this.formGroup?.get(fieldId)?.value === optionValue;
  }

  onSingleCheckboxChange(fieldId: string, optionValue: any, checked: boolean): void {
    const control = this.formGroup?.get(fieldId);
    if (!control || control.disabled) return;

    this.setControlValueAndMarkDirty(control, checked ? optionValue : null);
  }

  // ───────────────────────────────────────────────
  //  Conditional logic & auto-calculations
  // ───────────────────────────────────────────────

  private handleConditionalLogic(): void {
    for (const section of this.formConfig.sections) {
      const sectionVisible = this.resolveVisibility(section.conditionalLogic);

      for (const field of section.fields) {
        if (field.type === 'table') {
          this.syncTableRowState(field, sectionVisible && this.resolveVisibility(field.conditionalLogic));
          continue;
        }

        const control = this.formGroup.get(field.id);
        if (!control) continue;

        const fieldVisible = this.resolveVisibility(field.conditionalLogic);
        const shouldShow = sectionVisible && fieldVisible;
        const shouldRemainDisabled = !!field.disabled || !!field.autoCalculate || this.readonly;
        const enabledByLogic = this.resolveEnablement(field.conditionalLogic);

        if (shouldShow) {
          if (shouldRemainDisabled || !enabledByLogic) {
            control.disable({ emitEvent: false });
            // Gated by an 'enable' rule that no longer passes — drop the stale value.
            if (!enabledByLogic && control.value !== null) {
              control.setValue(null, { emitEvent: false });
            }
          } else {
            control.enable({ emitEvent: false });
          }
        } else {
          // If another field sharing the same ID is visible, skip disabling —
          // the shared control will be managed by that visible sibling.
          const hasSiblingVisible = section.fields.some(
            f => f !== field && f.id === field.id && this.resolveVisibility(f.conditionalLogic)
          );
          if (!hasSiblingVisible) {
            control.disable({ emitEvent: false });
            control.setValue(null, { emitEvent: false });
          }
        }
      }
    }
  }

  private resolveVisibility(logic?: ConditionalLogic[]): boolean {
    if (!logic?.length) return true;

    const showRules = logic.filter(l => !l.action || l.action === 'show');
    const hideRules = logic.filter(l => l.action === 'hide');

    const showVisible = !showRules.length || this.evaluateConditionalLogic(showRules);
    const hideVisible = !hideRules.length || !this.evaluateConditionalLogic(hideRules);

    return showVisible && hideVisible;
  }

  /** A field with no 'enable' rules is always enabled; otherwise every rule must pass. */
  private resolveEnablement(logic?: ConditionalLogic[]): boolean {
    const enableRules = logic?.filter(l => l.action === 'enable') ?? [];
    return !enableRules.length || this.evaluateConditionalLogic(enableRules);
  }

  private evaluateConditionalLogic(logic: ConditionalLogic[]): boolean {
    // Group conditions by field — conditions on the same field are OR'd,
    // conditions across different fields are AND'd.
    const grouped = new Map<string, ConditionalLogic[]>();
    for (const condition of logic) {
      const existing = grouped.get(condition.field);
      if (existing) {
        existing.push(condition);
      } else {
        grouped.set(condition.field, [condition]);
      }
    }

    return Array.from(grouped.values()).every(conditions =>
      conditions.some(condition => this.evaluateCondition(condition))
    );
  }

  private evaluateCondition(condition: ConditionalLogic): boolean {
    const fieldValue = this.formGroup.get(condition.field)?.value;

    switch (condition.operator) {
      case 'equals':       return fieldValue === condition.value;
      case 'notEquals':    return fieldValue !== condition.value;
      case 'includes':     return Array.isArray(fieldValue) && fieldValue.includes(condition.value);
      case 'notIncludes':  return !Array.isArray(fieldValue) || !fieldValue.includes(condition.value);
      case 'greaterThan':  return Number(fieldValue) > Number(condition.value);
      case 'lessThan':     return Number(fieldValue) < Number(condition.value);
      default:             return true;
    }
  }

  private handleAutoCalculations(): void {
    this.forEachField((field) => {
      if (!this.isFieldVisible(field)) return;

      const control = this.formGroup.get(field.id);
      if (!control) return;

      if (field.autoCalculate) {
        control.setValue(this.evaluateFormula(field.autoCalculate), { emitEvent: false });
      } else if (field.stringAutoCalculate) {
        control.setValue(this.evaluateStringFormula(field.stringAutoCalculate), { emitEvent: false });
      }
    });
  }

  private evaluateStringFormula(formula: string): string {
    let result = formula;
    const fieldRefs = formula.match(/\$\s*[\w\s]+/g) ?? [];

    for (const ref of fieldRefs) {
      const fieldId = ref.substring(1).trim();
      const value = this.formGroup.get(fieldId)?.value;
      if (value === null || value === undefined || value === '') return '';
      result = result.replace(ref, String(value));
    }

    return result;
  }

  private evaluateFormula(formula: string): number {
    let expression = formula;
    //$Regular Withholding + $Estimated Deductions
    //should match and $Regular Withholding and $Estimated Deductions as field references
    // formula can have *, /, +, - operators and parentheses, but for simplicity we will just replace field references with their values and use eval (or Function) to compute the result
    const fieldRefs = formula.match(/\$\s*[\w\s]+/g);

    if (fieldRefs) {
      for (const ref of fieldRefs) {
        const value = this.formGroup.get(ref.substring(1)?.trim())?.value || 0;
        expression = expression.replace(ref, String(value));
      }
    }

    try {
      const result = new Function('return ' + expression)();
      return isNaN(result) ? 0 : result;
    } catch {
      return 0;
    }
  }

  // ───────────────────────────────────────────────
  //  Visibility (used by template)
  // ───────────────────────────────────────────────

  isFieldVisible(field: DynamicFormField): boolean {
    return this.resolveVisibility(field.conditionalLogic);
  }

  isSectionVisible(section: FormSection): boolean {
    return this.resolveVisibility(section.conditionalLogic);
  }

  /**
   * A single-option checkbox already renders its text beside the box, so a legend
   * repeating the same words is just noise — only show it when it adds something.
   */
  showCheckboxLegend(field: DynamicFormField): boolean {
    if (!field.options?.length) return false;
    if (field.options.length > 1) return true;
    return field.label !== field.options[0].label;
  }

  /** True when an 'enable' rule is currently holding the field closed. */
  isFieldGatedOff(field: DynamicFormField): boolean {
    return !this.resolveEnablement(field.conditionalLogic);
  }

  getFormattedValue(field: DynamicFormField): string {
    const value = this.formGroup.get(field.id)?.value;
    if (field.displayFormula) {
      return field.displayFormula.replace('${value}', String(value));
    }
    return String(value || '');
  }

  /** Field label for display; the Tax Exemptions "Part 1" question gets the employer's name appended. */
  getFieldLabel(field: DynamicFormField): string {
    if (this.formConfig?.name === 'tax-exemptions' && field.id === 'Part 1') {
      const employerName = this.getEmployerName();
      if (employerName) return `${field.label} (${employerName})`;
    }
    return field.label;
  }

  private getEmployerName(): string {
    const employer = this.checklistItem?.details?.find(d => d.type === 'EMP');
    if (!employer) return '';
    return [employer.firstName, employer.lastName].filter(Boolean).join(' ').trim();
  }

  // ───────────────────────────────────────────────
  //  Save
  // ───────────────────────────────────────────────

  onSave(isAssociation: boolean): void {
    if (!this.formGroup.valid) {
      this.markAllFieldsTouched();
      this.scrollToFirstError();
      return;
    }

    const checklistItem = this.checklistItem;
    if (!checklistItem) return;

    this.saveInProgress = true;
    this.saveError = false;

    const formValue = this.transformFormValueToLabelBased(this.formGroup.getRawValue());
    const associationIds = this.getAssociationIds();

    if (this.linkedSaveConfig) {
      this.saveLinked(checklistItem, formValue, associationIds);
    } else if (isAssociation) {
      this.saveAssociation(checklistItem, formValue, associationIds);
    } else {
      this.saveStandard(checklistItem, formValue, associationIds);
    }
  }

  private saveLinked(checklistItem: any, formValue: any, _associationIds: any[]): void {
    const cfg = this.linkedSaveConfig!;
    const payload = {
      type: 'employerProvider',
      action: checklistItem.formMasterId === 113 ? 'sign' : 'save',
      checklistItems: [{
        providerType: 'IR',
        providerId: Number(cfg.providerId),
        formValue,
        checklistDetailId: checklistItem.checklistItemDetailId,
        checklistId: checklistItem.checklistItemId,
      }],
    };

    const save$ = cfg.mode === 'employerProvider'
      ? this.checkListService.saveEmployerProviderForm(this.programCode, cfg.employerId, cfg.providerId, cfg.userType, payload)
      : this.checkListService.saveProviderEmployerForm(this.programCode, cfg.employerId, cfg.providerId, cfg.userType, payload);

    save$.subscribe({
      next: (response: HttpResponse<Blob>) => {
        if (response.status === 200) {
          this.handleSaveSuccess(checklistItem);
        }
      },
      error: () => {
        this.handleSaveError(checklistItem);
      },
    });
  }

  onEdit(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      const field = this.findFieldById(key);
      if (control && !field?.disabled && !field?.autoCalculate && !this.readonly) {
        control.enable({ emitEvent: false });
      }
    });

    this.handleConditionalLogic();

    // Focus on first editable field for keyboard accessibility
    setTimeout(() => {
      const selector = '.dynamic-form input:not([disabled]), .dynamic-form select:not([disabled]), .dynamic-form textarea:not([disabled])';
      (document.querySelector(selector) as HTMLElement)?.focus();
    }, 100);
  }

  get canSave(): boolean {
    return !this.readonly && !this.isFormCompleted && this.hasChanges && this.formGroup.valid;
  }

  // ───────────────────────────────────────────────
  //  Validation messages & accessibility
  // ───────────────────────────────────────────────

  getErrorMessage(field: DynamicFormField): string {
    const control = this.formGroup.get(field.id);
    if (!control?.touched || !control.errors) return '';

    if (control.errors['required']) return `${field.label} is required`;
    if (control.errors['invalidOption']) return `${field.label} is required`;
    if (control.errors['email'])    return 'Please enter a valid email address';
    if (control.errors['min'])      return `Minimum value is ${control.errors['min'].min}`;
    if (control.errors['max'])      return `Maximum value is ${control.errors['max'].max}`;
    if (control.errors['allZeros']) {
      return 'Value cannot be all zeros.';
    }
    if (control.errors['abaRouting']) {
      return 'Invalid routing number.';
    }
    if (control.errors['heightBothZero']) {
      return 'Height cannot be 0 feet and 0 inches.';
    }
    if (control.errors['taxpayerIdValueMissing']) {
      const type = control.errors['taxpayerIdValueMissing'] === TAX_ID_TYPE.EIN ? TAX_ID_TYPE.EIN : TAX_ID_TYPE.SSN;
      return `${type} value is not present in the system. Please add it under Identifiers in the Enrollment Information tab.`;
    }
    if (control.errors['pattern'])  return field.validation?.find(v => v.type === 'pattern')?.message || 'Invalid format';

    return '';
  }

  getAriaDescribedBy(field: DynamicFormField): string | null {
    const parts: string[] = [];
    if (field.description) parts.push(`desc-${field.id}`);
    if (this.getErrorMessage(field)) parts.push(`error-${field.id}`);
    return parts.length ? parts.join(' ') : null;
  }

  // ───────────────────────────────────────────────
  //  Save helpers (private)
  // ───────────────────────────────────────────────

  private saveAssociation(checklistItem: any, formValue: any, associationIds: any[]): void {
    const payload = {
      type: 'participantProvider',
      action: checklistItem.formMasterId === 113 ? 'sign' : 'save',
      checklistItems: [{
        associationId: associationIds,
        participantId: this.selectedParticipantId,
        providerType: this.selectedProviderType,
        providerId: this.providerId,
        formValue,
        checklistDetailId: checklistItem.checklistItemDetailId,
        checklistId: checklistItem.checklistItemId,
      }],
    };

    this.checkListService
      .saveParticipantAssocaitionForms(
        this.programCode,
        this.selectedParticipantId,
        this.providerId,
        this.selectedProviderType || '',
        payload,
      )
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          if (response.status === 200) {
            this.handleSaveSuccess(checklistItem);
          }
        },
        error: () => {
          this.handleSaveError(checklistItem);
        },
      });
  }

  private saveStandard(checklistItem: any, formValue: any, associationIds: any[]): void {
    const currentUser = checklistItem.details.filter((d: any) => d.associationId == -1);

    const checklistItems = [{
      checklistId: checklistItem.checklistItemId,
      checklistDetailId: checklistItem.checklistItemDetailId,
      participantId: this.isParticipantFlow ? this.userId : '',
      providerId: !this.isParticipantFlow ? this.userId : '',
      providerType: currentUser.length > 0 ? currentUser[0].type : '',
      associationId: associationIds,
      formValue,
    }];

    const payload = {
      type: this.source,
      action: 'save',
      checklistItems,
    };

    this.checkListService
      .saveParticipantProviderForms(this.programCode, this.userId, this.isParticipantFlow, payload)
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          if (response.status === 200) {
            this.handleSaveSuccess(checklistItem);
          }
        },
        error: () => {
          this.handleSaveError(checklistItem);
        },
      });
  }

  private handleSaveSuccess(checklistItem: ChecklistItem): void {
    const isCurrentChecklist =
      this.checklistItem?.checklistItemId === checklistItem.checklistItemId &&
      this.checklistItem?.checklistItemDetailId === checklistItem.checklistItemDetailId;

    if (isCurrentChecklist) {
      this.saveInProgress = false;
      this.isFormCompleted = false;
      this.hasChanges = false;
      this.formGroup.markAsPristine();
      this.formChanged.emit(false);
    }

    this.formSaved.emit({
      checklistItemId: checklistItem.checklistItemId,
      checklistItemDetailId: checklistItem.checklistItemDetailId,
    });
  }

  private handleSaveError(checklistItem: ChecklistItem): void {
    const isCurrentChecklist =
      this.checklistItem?.checklistItemId === checklistItem.checklistItemId &&
      this.checklistItem?.checklistItemDetailId === checklistItem.checklistItemDetailId;

    if (!isCurrentChecklist) {
      return;
    }

    this.saveInProgress = false;
    this.saveError = true;
    this.errorMessage = 'Failed to save the form. Please try again.';
  }

  private getAssociationIds(): any[] {
    return Array.from(
      new Set(
        (this.checklistItem?.details ?? [])
          .filter((e: any) => e.associationId != -1)
          .map((d: any) => d.associationId),
      ),
    );
  }

  // ───────────────────────────────────────────────
  //  Form value transformation (outbound)
  // ───────────────────────────────────────────────

  /** Transform form values to the label-based structure the backend expects. */
  private transformFormValueToLabelBased(formValue: any): any {
    const transformed: any = {};

    this.forEachField((field) => {
      // A table is sent as a single key holding a JSON array — one object per row the
      // user actually added, keyed by column id. The unused slots up to maxRows are
      // left out entirely.
      //
      // The array is base64-encoded on the way out: as raw JSON its escaped punctuation
      // trips the gateway WAF's special-character anomaly rule past ~2 rows, which
      // rejects the whole save with a 403. parseSavedTableRows reads either form back, so
      // it doesn't matter whether the backend decodes before storing or keeps the base64.
      if (field.type === 'table') {
        transformed[field.id] = encodeBase64(
          JSON.stringify(this.buildTableRowsForSave(field, formValue)),
        );
        return;
      }

      const value = formValue[field.id];

      // Fields with mergeInto contribute their value to another field's key.
      // If the checkbox is checked, its selected value overwrites the target field.
      if (field.mergeInto) {
        const isArray = Array.isArray(value);
        const isChecked = isArray ? value.length > 0 : !!value;
        if (isChecked) {
          transformed[field.mergeInto] = isArray ? value[0] : value;
        }
        return;
      }

      // Skip if a mergeInto field already set this key.
      if (transformed[field.id] !== undefined) return;

      if (field.type === 'date' && value) {
        transformed[field.id] = this.formatValueForSave(field.type, value);
      } else if (field.type === 'checkbox' && Array.isArray(field.options)) {
        this.transformCheckboxOptions(field, value, transformed);
      } else {
        transformed[field.id] = value ?? '';
      }
    });

    // W-9: the real "Social security number 1/2/3"/"Employer identification number 1/2" fields
    // (unmasked, matched by businessName against the actual signed PDF's Form_Fields) have no
    // conditionalLogic — kept permanently populated so a toggle back to a type re-displays
    // correctly — so getRawValue() always includes both types' values. Only the
    // currently-selected Taxpayer ID type should reach the sign form: blank out the other
    // type's real PDF fields (this is what the PDF editor actually reads).
    const tinType = transformed[W9_FIELD_IDS.TAX_ID_TYPE];
    if (tinType === TAX_ID_TYPE.SSN) {
      for (const id of [W9_FIELD_IDS.EIN_SEGMENT_1, W9_FIELD_IDS.EIN_SEGMENT_2]) {
        if (id in transformed) transformed[id] = '';
      }
    } else if (tinType === TAX_ID_TYPE.EIN) {
      for (const id of [W9_FIELD_IDS.SSN_SEGMENT_1, W9_FIELD_IDS.SSN_SEGMENT_2, W9_FIELD_IDS.SSN_SEGMENT_3]) {
        if (id in transformed) transformed[id] = '';
      }
    }

    // "SSN Value"/"EIN Value" and their computed "Social Security Number"/"Employer
    // Identification Number" display fields only ever hold the masked value from
    // getAssociationsDetails (e.g. "XXX-XX-7894") — never send them to the backend at all.
    for (const id of [W9_FIELD_IDS.SSN_VALUE, W9_FIELD_IDS.EIN_VALUE, W9_FIELD_IDS.SSN_DISPLAY, W9_FIELD_IDS.EIN_DISPLAY]) {
      delete transformed[id];
    }

    return transformed;
  }

  /**
   * Collect the rows the user added as plain objects keyed by column id. A column that
   * stores an id also contributes the readable label it was picked by, since the form is
   * the only place that can resolve one into the other.
   */
  private buildTableRowsForSave(field: DynamicFormField, formValue: any): Record<string, any>[] {
    const rows: Record<string, any>[] = [];

    for (let row = 1; row <= this.getRowCount(field); row++) {
      const rowValues: Record<string, any> = {};

      for (const column of field.columns ?? []) {
        const controlName = this.getCellControlName(field, column, row);
        const value = this.formatValueForSave(column.type, formValue[controlName]);

        // Resolve the readable text from what the user actually picked, before the id
        // guard below has a chance to drop it.
        const label = column.labelKey ? this.getCellDisplayValue(controlName, value) : null;

        rowValues[column.id] = this.toStoredColumnValue(column, value);

        if (column.labelKey) {
          rowValues[column.labelKey] = label;
        }
      }

      rows.push(rowValues);
    }

    return rows;
  }

  /**
   * A column that names an `optionsValueKey` stores an authorization id, so anything that
   * isn't one is sent as null rather than as prose in an id column.
   *
   * This guards a value the dropdown can no longer produce: rows saved while null-id
   * authorizations were still selectable hold the description in the id column, and the
   * prefill deliberately keeps an unrecognised saved value selectable so it isn't lost on
   * screen. Re-saving such a row now clears the id instead of carrying the text back. The
   * readable text is unaffected — it still goes out under the column's `labelKey`.
   *
   * An empty cell keeps its existing empty-string value; only a populated non-id is nulled.
   */
  private toStoredColumnValue(column: TableColumn, value: any): any {
    if (!column.optionsValueKey || value === '' || value === null || value === undefined) {
      return value;
    }

    const text = String(value).trim();
    return text !== '' && Number.isFinite(Number(text)) ? value : null;
  }

  /** A cell's on-screen text: the selected option's label for dropdowns, the raw value otherwise. */
  private getCellDisplayValue(controlName: string, value: any): any {
    if (value === '' || value === null || value === undefined) return '';

    const cell = this.cellFields.get(controlName);
    if (!cell?.options && !cell?.optionsSource) return value;

    const match = this.getFieldOptions(cell).find(option => String(option.value) === String(value));
    return match ? match.label : value;
  }

  /** Dates are serialised to the backend's string format; everything else falls back to ''. */
  private formatValueForSave(type: string, value: any): any {
    if (type === 'date' && value) {
      return value instanceof Date ? this.dateFormatter.formatDateToString(value) : value;
    }
    return value ?? '';
  }

  /** Expand each checkbox option into a separate key with 'Yes' / '' value. */
  private transformCheckboxOptions(field: DynamicFormField, value: any, target: any): void {
    for (const option of field.options!) {
      const key = option.key ?? option.label;
      const isSelected = Array.isArray(value)
        ? value.includes(option.value)
        : value === option.value;
      target[key] = isSelected ? option.value : '';
    }
  }

  // ───────────────────────────────────────────────
  //  Utility (private)
  // ───────────────────────────────────────────────

  /** Iterate over every field across all sections. */
  private forEachField(callback: (field: DynamicFormField) => void): void {
    for (const section of this.formConfig.sections) {
      for (const field of section.fields) {
        callback(field);
      }
    }
  }

  private findFieldById(fieldId: string): DynamicFormField | undefined {
    const cellField = this.cellFields.get(fieldId);
    if (cellField) return cellField;

    for (const section of this.formConfig.sections) {
      const field = section.fields.find(f => f.id === fieldId);
      if (field) return field;
    }
    return undefined;
  }

  /** Set a control's value, mark it dirty/touched, and emit the change. */
  private setControlValueAndMarkDirty(control: any, value: any): void {
    control.setValue(value);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
    this.emitDirtyState();
  }

  private emitDirtyState(): void {
    this.hasChanges = this.formGroup.dirty;
    this.formChanged.emit(this.hasChanges);
  }

  private markAllFieldsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].markAsTouched();
    });
  }

  private scrollToFirstError(): void {
    setTimeout(() => {
      const errorEl = document.querySelector('.dynamic-form .error-message');
      errorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  preventDecimalInput(event: KeyboardEvent): void {
    if (['.', ',', 'e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }
}
